import type { AuthUser } from '@repo/api-client';

const USER_KEY = 'qm_user';

/**
 * Roles that the backend grants DELETE permission to on
 * `DELETE /v1/admin/sets/:id`. Mirror of the controller's `DELETE_ROLES`.
 */
const DELETE_ROLES = new Set(['super-admin', 'senior']);

/**
 * Returns the cached user's role string from localStorage, or `null` if no
 * user is cached. NOT a hook — synchronous read, intentionally light so it
 * works for read-only gates like the SetCard delete button.
 *
 * Per app CLAUDE.md, `useMe` is exported but not yet consumed; this is the
 * cheap interim path for role-aware UI until a session-restore hook lands.
 */
export function getCurrentRole(): string | null {
  return getCachedUser()?.role ?? null;
}

/** True iff the cached user can hard-delete a QuestionSet (super-admin / senior). */
export function canDeleteSets(): boolean {
  const role = getCurrentRole();
  return role !== null && DELETE_ROLES.has(role);
}

export function cacheUser(user: AuthUser): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function getCachedUser(): AuthUser | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

export function clearUser(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(USER_KEY);
}
